import axios, { AxiosResponse } from 'axios';
import {
  User,
  Customer,
  CustomerCreate,
  Project,
  ProjectCreate,
  ProjectUpdate,
  Document,
  Survey,
  SurveyCreate,
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  ProjectStats,
  ListResponse,
  InventoryItem,
  InventoryCreate,
  InventoryUpdate,
  InventoryCategory,
  Quote,
  QuoteCreate,
  QuoteUpdate,
  EPCLookupResult,
  QuoteRequest,
  QuoteRequestCreate,
  QuoteRequestStatus,
} from '..';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API response wrapper
interface ApiResponse<T> {
  data: T;
}

// Auth API
export const authApi = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.post('/auth/login', credentials);
    return response.data;
  },

  register: async (userData: RegisterRequest): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.post('/auth/register', userData);
    return response.data;
  },

  getCurrentUser: async (): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.get('/auth/me');
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },
};

// Users API
export const usersApi = {
  getUsers: async (params?: { skip?: number; limit?: number; role?: string; search?: string }): Promise<User[]> => {
    const response: AxiosResponse<User[]> = await api.get('/users/', { params });
    return response.data;
  },

  getUser: async (userId: number): Promise<User> => {
    const response: AxiosResponse<User> = await api.get(`/users/${userId}`);
    return response.data;
  },

  updateUser: async (userId: number, userData: Partial<User>): Promise<User> => {
    const response: AxiosResponse<User> = await api.put(`/users/${userId}`, userData);
    return response.data;
  },

  deleteUser: async (userId: number): Promise<void> => {
    await api.delete(`/users/${userId}`);
  },

  getAvailableSurveyors: async (): Promise<User[]> => {
    const response: AxiosResponse<User[]> = await api.get('/users/surveyors/available');
    return response.data;
  },
};

// Customers API
export const customersApi = {
  getCustomers: async (params?: { skip?: number; limit?: number; search?: string }): Promise<{ customers: Customer[]; total: number }> => {
    const response: AxiosResponse<{ customers: Customer[]; total: number }> = await api.get('/customers/', { params });
    return response.data;
  },

  getCustomer: async (customerId: number): Promise<Customer> => {
    const response: AxiosResponse<Customer> = await api.get(`/customers/${customerId}`);
    return response.data;
  },

  createCustomer: async (customerData: CustomerCreate): Promise<Customer> => {
    const response: AxiosResponse<Customer> = await api.post('/customers/', customerData);
    return response.data;
  },

  updateCustomer: async (customerId: number, customerData: Partial<CustomerCreate>): Promise<Customer> => {
    const response: AxiosResponse<Customer> = await api.put(`/customers/${customerId}`, customerData);
    return response.data;
  },

  deleteCustomer: async (customerId: number): Promise<void> => {
    await api.delete(`/customers/${customerId}`);
  },
};

// Projects API
export const projectsApi = {
  getProjects: async (params?: { 
    skip?: number; 
    limit?: number; 
    status?: string; 
    project_type?: string; 
    search?: string;
  }): Promise<{ projects: Project[]; total: number }> => {
    const response: AxiosResponse<{ projects: Project[]; total: number }> = await api.get('/projects/', { params });
    return response.data;
  },

  getProject: async (projectId: number): Promise<Project> => {
    const response: AxiosResponse<Project> = await api.get(`/projects/${projectId}`);
    return response.data;
  },

  createProject: async (projectData: ProjectCreate): Promise<Project> => {
    const response: AxiosResponse<Project> = await api.post('/projects/', projectData);
    return response.data;
  },

  updateProject: async (projectId: number, projectData: ProjectUpdate): Promise<Project> => {
    const response: AxiosResponse<Project> = await api.put(`/projects/${projectId}`, projectData);
    return response.data;
  },

  deleteProject: async (projectId: number): Promise<void> => {
    await api.delete(`/projects/${projectId}`);
  },

  getProjectStats: async (): Promise<ProjectStats> => {
    const response: AxiosResponse<ProjectStats> = await api.get('/projects/stats/dashboard');
    return response.data;
  },
};

// Documents API
export const documentsApi = {
  getDocuments: async (params?: { 
    project_id?: number; 
    document_type?: string; 
    skip?: number; 
    limit?: number;
  }): Promise<{ documents: Document[]; total: number }> => {
    const response: AxiosResponse<{ documents: Document[]; total: number }> = await api.get('/documents/', { params });
    return response.data;
  },

  getDocument: async (documentId: number): Promise<Document> => {
    const response: AxiosResponse<Document> = await api.get(`/documents/${documentId}`);
    return response.data;
  },

  uploadDocument: async (
    projectId: number,
    file: File,
    documentType: string,
    title?: string,
    description?: string,
    isCustomerVisible?: boolean
  ): Promise<Document> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId.toString());
    formData.append('document_type', documentType);
    if (title) formData.append('title', title);
    if (description) formData.append('description', description);
    if (isCustomerVisible !== undefined) formData.append('is_customer_visible', isCustomerVisible.toString());

    const response: AxiosResponse<Document> = await api.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  downloadDocument: async (documentId: number): Promise<Blob> => {
    const response: AxiosResponse<Blob> = await api.get(`/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  deleteDocument: async (documentId: number): Promise<void> => {
    await api.delete(`/documents/${documentId}`);
  },
};

// Surveys API
export const surveysApi = {
  getSurveys: async (params?: { 
    skip?: number; 
    limit?: number; 
    status?: string; 
    project_id?: number;
    surveyor_id?: number;
  }): Promise<{ surveys: Survey[]; total: number }> => {
    const response: AxiosResponse<{ surveys: Survey[]; total: number }> = await api.get('/surveys/', { params });
    return response.data;
  },

  getSurvey: async (surveyId: number): Promise<Survey> => {
    const response: AxiosResponse<Survey> = await api.get(`/surveys/${surveyId}`);
    return response.data;
  },

  createSurvey: async (surveyData: SurveyCreate): Promise<Survey> => {
    const response: AxiosResponse<Survey> = await api.post('/surveys/', surveyData);
    return response.data;
  },

  updateSurvey: async (surveyId: number, surveyData: Partial<Survey>): Promise<Survey> => {
    const response: AxiosResponse<Survey> = await api.put(`/surveys/${surveyId}`, surveyData);
    return response.data;
  },

  deleteSurvey: async (surveyId: number): Promise<void> => {
    await api.delete(`/surveys/${surveyId}`);
  },

  startSurvey: async (surveyId: number): Promise<Survey> => {
    const response: AxiosResponse<Survey> = await api.post(`/surveys/${surveyId}/start`);
    return response.data;
  },

  completeSurvey: async (surveyId: number): Promise<Survey> => {
    const response: AxiosResponse<Survey> = await api.post(`/surveys/${surveyId}/complete`);
    return response.data;
  },
};

// Inventory API
export const inventoryApi = {
  getInventory: async (params?: {
    skip?: number;
    limit?: number;
    category?: InventoryCategory;
    in_stock_only?: boolean;
    active_only?: boolean;
    search?: string;
  }): Promise<{ items: InventoryItem[]; total: number }> => {
    const response: AxiosResponse<{ items: InventoryItem[]; total: number }> =
      await api.get('/inventory/', { params });
    return response.data;
  },

  getInventoryItem: async (itemId: number): Promise<InventoryItem> => {
    const response: AxiosResponse<InventoryItem> = await api.get(`/inventory/${itemId}`);
    return response.data;
  },

  createInventoryItem: async (itemData: InventoryCreate): Promise<InventoryItem> => {
    const response: AxiosResponse<InventoryItem> = await api.post('/inventory/', itemData);
    return response.data;
  },

  updateInventoryItem: async (itemId: number, itemData: InventoryUpdate): Promise<InventoryItem> => {
    const response: AxiosResponse<InventoryItem> = await api.put(`/inventory/${itemId}`, itemData);
    return response.data;
  },

  deleteInventoryItem: async (itemId: number): Promise<void> => {
    await api.delete(`/inventory/${itemId}`);
  },
};

// Quotes API
export const quotesApi = {
  epcLookup: async (postcode: string, address?: string): Promise<EPCLookupResult> => {
    const response: AxiosResponse<EPCLookupResult> = await api.get('/quotes/epc-lookup', {
      params: { postcode, address },
    });
    return response.data;
  },

  getQuotes: async (params?: {
    project_id?: number;
    status?: string;
    skip?: number;
    limit?: number;
  }): Promise<{ quotes: Quote[]; total: number }> => {
    const response: AxiosResponse<{ quotes: Quote[]; total: number }> =
      await api.get('/quotes/', { params });
    return response.data;
  },

  getQuote: async (quoteId: number): Promise<Quote> => {
    const response: AxiosResponse<Quote> = await api.get(`/quotes/${quoteId}`);
    return response.data;
  },

  createQuote: async (data: QuoteCreate): Promise<Quote> => {
    const response: AxiosResponse<Quote> = await api.post('/quotes/', data);
    return response.data;
  },

  updateQuote: async (quoteId: number, data: QuoteUpdate): Promise<Quote> => {
    const response: AxiosResponse<Quote> = await api.put(`/quotes/${quoteId}`, data);
    return response.data;
  },

  deleteQuote: async (quoteId: number): Promise<void> => {
    await api.delete(`/quotes/${quoteId}`);
  },

  downloadPdf: async (quoteId: number, quoteNumber: string): Promise<void> => {
    const response = await api.get(`/quotes/${quoteId}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${quoteNumber}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },
};

// Quote Requests API
export const quoteRequestsApi = {
  // Public — no auth required
  createPublic: async (data: QuoteRequestCreate): Promise<QuoteRequest> => {
    const response: AxiosResponse<QuoteRequest> = await api.post('/quote-requests/', data);
    return response.data;
  },

  // Authenticated customer submitting via portal
  createAuthenticated: async (data: QuoteRequestCreate): Promise<QuoteRequest> => {
    const response: AxiosResponse<QuoteRequest> = await api.post('/quote-requests/authenticated', data);
    return response.data;
  },

  // Manager list
  getRequests: async (params?: {
    status?: QuoteRequestStatus;
    search?: string;
    skip?: number;
    limit?: number;
  }): Promise<{ requests: QuoteRequest[]; total: number }> => {
    const response: AxiosResponse<{ requests: QuoteRequest[]; total: number }> =
      await api.get('/quote-requests/', { params });
    return response.data;
  },

  // Current customer's own requests
  getMyRequests: async (): Promise<{ requests: QuoteRequest[]; total: number }> => {
    const response: AxiosResponse<{ requests: QuoteRequest[]; total: number }> =
      await api.get('/quote-requests/my');
    return response.data;
  },

  getRequest: async (id: number): Promise<QuoteRequest> => {
    const response: AxiosResponse<QuoteRequest> = await api.get(`/quote-requests/${id}`);
    return response.data;
  },

  updateRequest: async (id: number, data: { status?: QuoteRequestStatus; quote_id?: number }): Promise<QuoteRequest> => {
    const response: AxiosResponse<QuoteRequest> = await api.put(`/quote-requests/${id}`, data);
    return response.data;
  },

  deleteRequest: async (id: number): Promise<void> => {
    await api.delete(`/quote-requests/${id}`);
  },
};

export default api;
